import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Play, Pause, RotateCcw, User, AlertCircle, CheckCircle, Share2, Lock, Info, Volume2, VolumeX, Music, Sparkles, Check, Gift, Heart, QrCode } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useGlobalBackgroundAudio } from '@/contexts/BackgroundAudioContext';
import { useEnhancedAudioManager } from '@/hooks/useEnhancedAudioManager';
import { apiClient } from '@/utils/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { ShareQRCode } from '@/components/sessions/ShareQRCode';

interface SharedSessionData {
  title: string;
  duration: number;
  practitioner_name: string;
  created_at: string;
  session_type: string;
  background_music_file?: string;
  has_mixed_audio?: boolean;  // Indicates if pre-mixed audio is available
  is_draft?: boolean;  // Draft sessions stream voice-only audio
  referral_code?: string;
  discount_percentage?: number;
  has_referral_code: boolean;
  // Gift-related fields
  is_gift?: boolean;
  sender_name?: string;
  gift_message?: string;
  // Membership info for loyalty program
  is_member?: boolean;
  member_since_year?: number;
}

export const SharedSession = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [sessionData, setSessionData] = useState<SharedSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasSessionEnded, setHasSessionEnded] = useState(false);

  // Audio playback state
  const [volume, setVolume] = useState(85);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [backgroundVolume, setBackgroundVolume] = useState(30);

  // Background audio for 20-second tail after session ends
  const {
    startAutoStopTimer,
    cancelAutoStopTimer
  } = useGlobalBackgroundAudio();

  // Construct streaming URL - use backend base URL for proper routing
  // Use useMemo to prevent URL from changing on every render
  const streamingUrl = useMemo(() => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
    return `${backendUrl}/audio/shared/${accessToken}/stream`;
  }, [accessToken]);

  // Debug logging
  useEffect(() => {
    console.log('🎵 SharedSession Audio Debug:', {
      streamingUrl,
      accessToken,
      hasSessionData: !!sessionData,
      backgroundMusicFile: sessionData?.background_music_file
    });
  }, [streamingUrl, accessToken, sessionData]);

  // Background music handling for shared sessions:
  // For sessions with background music, the backend now serves pre-mixed audio (voice + background combined)
  // This prevents exposing background music files publicly and ensures secure sharing
  // The has_mixed_audio flag indicates whether pre-mixed audio is available
  // If has_mixed_audio is true, the streaming URL already includes background music
  // If false or undefined, we fall back to voice-only audio
  const backgroundAudioUrl = useMemo(() => {
    // Pre-mixed audio is served by the backend streaming endpoint
    // No separate background music file needs to be loaded for shared sessions
    return null;
  }, []);

  // Enhanced audio manager with background music
  // IMPORTANT: Only recreate props when audio URLs actually change, not when sessionData changes
  const audioManagerProps = useMemo(() => ({
    mainAudioUrl: streamingUrl,
    backgroundAudioUrl,
    backgroundVolume: backgroundVolume / 100,
    autoPlayBackground: !!sessionData?.background_music_file
  }), [streamingUrl, backgroundAudioUrl, backgroundVolume, sessionData?.background_music_file]);

  // Note: We depend on sessionData?.background_music_file (the value) not sessionData (the object)
  // This prevents re-initialization when sessionData object changes but the actual values stay the same

  const {
    playWithBackground,
    pause,
    seekTo,
    isPlaying,
    isMainAudioReady,
    isBackgroundAudioReady,
    isBackgroundPlaying,
    isBackgroundMuted,
    updateMainVolume,
    updateBackgroundVolume,
    updatePlaybackRate,
    currentTime,
    duration,
    initializeAudioContext,
    stopBackgroundAudio,
    pauseBackgroundAudio: pauseMixedBackground,
    resumeBackgroundAudio: resumeMixedBackground
  } = useEnhancedAudioManager(audioManagerProps);

  // Update volumes
  useEffect(() => {
    const actualVolume = isMuted ? 0 : volume / 100;
    updateMainVolume(actualVolume);
  }, [volume, isMuted, updateMainVolume]);

  useEffect(() => {
    const actualBackgroundVolume = backgroundVolume / 100;
    updateBackgroundVolume(actualBackgroundVolume);
  }, [backgroundVolume, updateBackgroundVolume]);

  useEffect(() => {
    if (accessToken) {
      fetchSharedSession(accessToken);
    } else {
      setError('Invalid access link');
      setLoading(false);
    }
  }, [accessToken]);

  // Download prevention: Block right-click and keyboard shortcuts
  useEffect(() => {
    const preventDownload = (e: MouseEvent | KeyboardEvent) => {
      // Prevent right-click context menu
      if (e.type === 'contextmenu') {
        e.preventDefault();
        toast({
          title: "Download Disabled",
          description: "This audio can only be played on VoiceThera.",
          variant: "destructive",
        });
        return false;
      }

      // Prevent Ctrl+S / Cmd+S
      if (e instanceof KeyboardEvent && (e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        toast({
          title: "Download Disabled",
          description: "This audio can only be played on VoiceThera.",
          variant: "destructive",
        });
        return false;
      }
    };

    document.addEventListener('contextmenu', preventDownload as EventListener);
    document.addEventListener('keydown', preventDownload as EventListener);

    return () => {
      document.removeEventListener('contextmenu', preventDownload as EventListener);
      document.removeEventListener('keydown', preventDownload as EventListener);
    };
  }, [toast]);

  const fetchSharedSession = async (token: string) => {
    try {
      console.log('SharedSession: Processing token:', token);

      // Sanitize the token - remove any URL encoding or special characters
      const cleanToken = token.replace(/[<>"`']/g, '').trim();

      // Use backend API to get session data (no authentication required)
      const response = await apiClient.get(`/sessions/shared/${cleanToken}/info`, {
        includeAuth: false  // Public endpoint, no auth needed
      });

      console.log('Session API response:', response);

      if (!response) {
        throw new Error('This shared session link has expired, been deactivated, or is invalid.');
      }

      setSessionData(response);

      // Test the streaming URL to see if it's accessible
      console.log('🎵 Testing streaming URL accessibility...');
      const backendUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
      const testStreamUrl = `${backendUrl}/audio/shared/${cleanToken}/stream`;

      try {
        const testResponse = await fetch(testStreamUrl, {
          method: 'HEAD',
          mode: 'cors'
        });
        console.log('🎵 Stream URL test response:', {
          url: testStreamUrl,
          status: testResponse.status,
          statusText: testResponse.statusText,
          ok: testResponse.ok
        });

        if (!testResponse.ok && testResponse.status === 404) {
          console.error('❌ Stream endpoint returned 404. Session audio may be missing.');
          toast({
            title: "Audio Not Found",
            description: "The audio file for this session is not available. It may have been deleted or moved.",
            variant: "destructive"
          });
        }
      } catch (testError) {
        console.log('⚠️ Could not test stream URL (HEAD not supported), will try loading anyway');
      }

      // Background audio is now automatically loaded by useEnhancedAudioManager
      // when sessionData.background_music_file is present

    } catch (error: any) {
      console.error('SharedSession: Error fetching shared session:', error);

      // Provide user-friendly error messages
      let userMessage = error.message || 'Failed to load session';

      if (error.message?.includes('JWT')) {
        userMessage = 'Authentication error. Please try accessing the link again.';
      } else if (error.message?.includes('network')) {
        userMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message?.includes('decode')) {
        userMessage = 'Invalid link format. Please check the link and try again.';
      } else if (error.message?.includes('404')) {
        userMessage = 'Session not found. The share link may be invalid or expired.';
      }

      setError(userMessage);
      setLoading(false);

      // Toast notification for better UX
      toast({
        variant: 'destructive',
        title: 'Session Access Error',
        description: userMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle session end (when audio finishes)
  useEffect(() => {
    if (!hasSessionEnded && !isPlaying && currentTime > 0 && duration > 0 && currentTime >= duration) {
      setHasSessionEnded(true);
      setIsCompleted(true);

      // Background music 20-second tail is handled by Enhanced Audio Manager
      // which automatically continues playing the background music after the voice ends
      startAutoStopTimer(20);
    }
  }, [hasSessionEnded, isPlaying, currentTime, duration, startAutoStopTimer]);

  // Reset session end state when starting to play
  useEffect(() => {
    if (isPlaying) {
      setHasSessionEnded(false);
    }
  }, [isPlaying]);

  const handlePlayPause = useCallback(async () => {
    if (!hasStarted) {
      setHasStarted(true);
    }

    if (isPlaying) {
      pause();
    } else {
      // Cancel any pending auto-stop timer when starting new playback
      cancelAutoStopTimer();

      try {
        console.log('🎵 Attempting to play audio:', {
          streamingUrl,
          backgroundAudioUrl,
          playbackRate,
          isMainAudioReady,
          isBackgroundAudioReady
        });

        await initializeAudioContext();
        await playWithBackground(playbackRate);

        console.log('✅ Audio playback started successfully');
      } catch (error) {
        console.error('❌ Playback failed:', error);
        console.error('Error details:', {
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          streamingUrl,
          backgroundAudioUrl
        });

        toast({
          title: "Playback Error",
          description: error instanceof Error ? error.message : "Failed to start audio. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [isPlaying, pause, playWithBackground, playbackRate, initializeAudioContext, cancelAutoStopTimer, hasStarted, toast, streamingUrl, backgroundAudioUrl, isMainAudioReady, isBackgroundAudioReady]);

  const handleRestart = useCallback(() => {
    seekTo(0);
  }, [seekTo]);

  const handleSeek = useCallback((value: number[]) => {
    seekTo(value[0]);
  }, [seekTo]);

  const handleSpeedChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    updatePlaybackRate(rate);
  }, [updatePlaybackRate]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);


  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Access Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full"
              variant="outline"
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Session not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card className={sessionData.is_gift ? "border-pink-200 bg-gradient-to-br from-pink-50/50 to-purple-50/50 dark:from-pink-950/20 dark:to-purple-950/20" : ""}>
          <CardHeader className="text-center">
            {sessionData.is_gift ? (
              <>
                {/* Gift Header */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-full mb-3">
                  <Heart className="h-4 w-4 fill-current" />
                  <span className="text-sm font-medium">
                    From: {sessionData.sender_name || 'Someone special'}
                  </span>
                </div>
                <CardTitle className="text-2xl mb-3">{sessionData.title}</CardTitle>
                {sessionData.gift_message && (
                  <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg mb-4 max-w-md mx-auto">
                    <p className="text-muted-foreground italic text-sm">
                      "{sessionData.gift_message}"
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Regular Session Header */}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    From {sessionData.practitioner_name}
                  </span>
                  {sessionData.is_member && sessionData.member_since_year && (
                    <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                      Member since {sessionData.member_since_year}
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-xl">{sessionData.title}</CardTitle>
              </>
            )}
            <div className="flex items-center justify-center gap-4 mt-4">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Permanent Link
              </Badge>
              {isCompleted && (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Completed
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Draft Session Warning */}
        {sessionData.is_draft && sessionData.background_music_file && (
          <Alert variant="default" className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700 dark:text-amber-400">Preview Mode</AlertTitle>
            <AlertDescription className="text-amber-600 dark:text-amber-300">
              This session is still being edited. You're hearing <strong>voice-only</strong> audio.
              Background music will be included once the creator saves the session.
            </AlertDescription>
          </Alert>
        )}

        {/* Audio Player */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Progress Bar with Seek */}
              <div className="space-y-2">
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={0.1}
                  className="w-full cursor-pointer"
                  disabled={!isMainAudioReady}
                  onValueChange={handleSeek}
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestart}
                  disabled={!hasStarted}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>

                <Button
                  size="lg"
                  onClick={handlePlayPause}
                  className="px-8"
                  disabled={!isMainAudioReady}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5 mr-2" />
                  ) : (
                    <Play className="h-5 w-5 mr-2" />
                  )}
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: sessionData.title,
                        text: `Listen to this meditation: ${sessionData.title}`,
                        url: window.location.href
                      }).catch(() => {});
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      toast({
                        title: "Link Copied!",
                        description: "Share this link with others.",
                      });
                    }
                  }}
                  title="Share this session"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Status Message */}
              {!isMainAudioReady && (
                <p className="text-sm text-center text-blue-600">⏳ Loading audio player...</p>
              )}

              {/* Volume Controls */}
              <div className="space-y-4">
                {/* Main Volume */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">Volume</div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleMute}
                      className="px-2"
                    >
                      {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </Button>
                    <Slider
                      value={[isMuted ? 0 : volume]}
                      max={100}
                      step={1}
                      onValueChange={(value) => setVolume(value[0])}
                      className="flex-1"
                    />
                    <span className="text-sm text-muted-foreground w-10 text-right">
                      {isMuted ? 0 : volume}%
                    </span>
                  </div>
                </div>

                {/* Background Music Volume - Only if background music exists */}
                {sessionData?.background_music_file && !sessionData?.has_mixed_audio && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Background Music Volume</div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={isBackgroundMuted ? resumeMixedBackground : pauseMixedBackground}
                        className="px-2"
                        disabled={!isBackgroundAudioReady}
                      >
                        {isBackgroundMuted ? <VolumeX className="h-4 w-4" /> : <Music className="h-4 w-4" />}
                      </Button>
                      <Slider
                        value={[backgroundVolume]}
                        max={100}
                        step={1}
                        onValueChange={(value) => setBackgroundVolume(value[0])}
                        className="flex-1"
                        disabled={isBackgroundMuted}
                      />
                      <span className="text-sm text-muted-foreground w-10 text-right">
                        {isBackgroundMuted ? 0 : backgroundVolume}%
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Playback Speed */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Playback Speed</span>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(playbackRate * 100)}%
                  </span>
                </div>
                <Slider
                  value={[playbackRate]}
                  min={0.6}
                  max={1.3}
                  step={0.01}
                  onValueChange={(value) => handleSpeedChange(value[0])}
                  className="w-full"
                />
                {/* Speed Preset Buttons */}
                <div className="flex flex-wrap gap-1 justify-center">
                  {[0.6, 0.7, 0.8, 0.85, 0.9, 0.95, 1.0, 1.1].map((rate) => (
                    <Button
                      key={rate}
                      variant={playbackRate === rate ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSpeedChange(rate)}
                      className="text-xs px-2 py-1 h-7"
                    >
                      {rate}x
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Public session - no feedback section for unauthenticated users */}

        {/* Connect with Practitioner CTA for authenticated users without referral code */}
        {user && sessionData?.has_referral_code && sessionData.referral_code && (
          <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-2">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">
                Connect with {sessionData.practitioner_name}
              </CardTitle>
              <CardDescription className="text-base mt-2">
                Link to {sessionData.practitioner_name}'s referral code to get {sessionData.discount_percentage}% off your future credit purchases
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-center">
                  <strong>Exclusive Offer:</strong> Get{' '}
                  <span className="text-primary font-semibold text-lg">{sessionData.discount_percentage}%</span>{' '}
                  off your next credit purchase
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate(`/apply-referral?ref=${sessionData.referral_code}`)}
                className="w-full text-base"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Apply {sessionData.discount_percentage}% Discount
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                You can only link to one practitioner
              </p>
            </CardContent>
          </Card>
        )}

        {/* Signup CTA for non-authenticated users */}
        {!user && (
          <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20 border-2 shadow-lg">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center items-center gap-2 mb-2">
                <Sparkles className="h-8 w-8 text-primary" />
                <Badge className="bg-green-500 text-white">FREE</Badge>
              </div>
              <CardTitle className="text-2xl">
                {sessionData?.has_referral_code && sessionData.discount_percentage
                  ? `Get ${sessionData.discount_percentage}% Off Your First Purchase!`
                  : sessionData?.is_gift && !sessionData?.has_referral_code
                  ? `Want More Personalized Meditations?`
                  : `Create Your Own Personalized Meditations`
                }
              </CardTitle>
              <CardDescription className="text-base mt-2">
                {sessionData?.has_referral_code && sessionData.discount_percentage
                  ? `${sessionData.practitioner_name} is offering you an exclusive discount. Sign up for free now and start creating AI-powered meditation sessions.`
                  : sessionData?.is_gift && !sessionData?.has_referral_code
                  ? `Loved this gift? Create your own AI-powered meditation sessions tailored to your needs.`
                  : `Experience AI-powered meditation sessions tailored just for you`
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Benefits List */}
              <div className="grid gap-3">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Custom meditation sessions for any situation</p>
                    <p className="text-sm text-muted-foreground">Anxiety, sleep, focus, motivation, and more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Choose your preferred voice and background music</p>
                    <p className="text-sm text-muted-foreground">Personalize every aspect of your practice</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Save and replay your favorites anytime</p>
                    <p className="text-sm text-muted-foreground">Build your personal meditation library</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Share sessions with clients (for practitioners)</p>
                    <p className="text-sm text-muted-foreground">Just like this one you're listening to!</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button
                  size="lg"
                  onClick={() => {
                    const signupUrl = sessionData?.referral_code
                      ? `/auth?mode=signup&ref=${sessionData.referral_code}`
                      : '/auth?mode=signup';
                    navigate(signupUrl);
                  }}
                  className="text-base px-8"
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  {sessionData?.has_referral_code && sessionData.discount_percentage
                    ? `Sign Up For Free`
                    : sessionData?.is_gift && !sessionData?.has_referral_code
                    ? `Try VoiceThera Free`
                    : `Start Free Trial`
                  }
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/auth?mode=signin')}
                  className="text-base px-8"
                >
                  Sign In
                </Button>
              </div>

              {/* Referral Code Badge */}
              {sessionData?.has_referral_code && sessionData.referral_code && sessionData.discount_percentage && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <p className="text-sm text-center">
                    <strong>Exclusive Offer:</strong> Use code{' '}
                    <code className="bg-primary/10 px-2 py-1 rounded text-primary font-mono font-semibold">
                      {sessionData.referral_code}
                    </code>{' '}
                    for {sessionData.discount_percentage}% off your first credit purchase
                  </p>
                </div>
              )}

              <p className="text-center text-sm text-muted-foreground">
                No credit card required • Start creating in minutes
              </p>
            </CardContent>
          </Card>
        )}

        {/* QR Code for Sharing */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Share This Session
            </CardTitle>
            <CardDescription>
              Scan or download the QR code to share with others
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ShareQRCode shareUrl={window.location.href} sessionTitle={sessionData.title} />

            {/* Signup prompt for non-authenticated users */}
            {!user && (
              <div className="text-center pt-3 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  Want to create your own personalized meditations?
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/auth?mode=signup')}
                >
                  Sign Up Free
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <Card>
          <CardContent className="pt-6">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>About This Shared Session</AlertTitle>
              <AlertDescription className="space-y-2 text-sm">
                <p>✓ This link never expires and can be shared freely</p>
                <p>✓ Audio can only be played on VoiceThera</p>
                <p>✓ Content is protected by copyright</p>
                <p className="text-xs text-muted-foreground mt-4">
                  © VoiceThera. All rights reserved.
                </p>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SharedSession;